import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import FormSkeleton from "../components/skeletons/FormSkeleton";
import { InlineLoadSkeleton } from "../components/common/InlineLoadSkeleton";
import { clientService } from "../services/clientService";
import { userService } from "../services/userService";
import rbacService from "../services/rbacService";
import toast from "react-hot-toast";

/** Matches chief-admin regardless of DB formatting (e.g. "Chief Admin", "chief-admin"). */
const isChiefRole = (roleStr) => {
  const k = String(roleStr || "")
    .toLowerCase()
    .trim()
    .replace(/[\s_]+/g, "-");
  return k === "chief-admin" || k === "chiefadmin";
};

const EditUserPage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    department: "",
    role: "",
    client_id: "",
  });

  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [roleOptions, setRoleOptions] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const departments = ["QA", "Product", "Dev", "Support"];

  const loadRoles = useCallback(async (clientId, currentUserRole = "") => {
    if (!clientId) {
      setRoleOptions([]);
      return;
    }
    try {
      setLoadingRoles(true);
      const response = await rbacService.getRoles({
        client_id: clientId,
        is_active: true,
      });
      if (response.success) {
        const rolesList = response.data?.roles || response.data || [];
        const formattedRoles = rolesList
          .filter((role) => role.is_active && !role.is_deleted)
          .map((role) => ({
            value: role.role_name,
            label: role.role_name,
          }));

        if (currentUserRole) {
          const exactMatch = formattedRoles.find(
            (r) => r.value === currentUserRole,
          );
          const caseInsensitiveMatch = formattedRoles.find(
            (r) => r.value.toLowerCase() === currentUserRole.toLowerCase(),
          );
          if (exactMatch) {
            setFormData((prev) => ({ ...prev, role: exactMatch.value }));
          } else if (caseInsensitiveMatch) {
            setFormData((prev) => ({
              ...prev,
              role: caseInsensitiveMatch.value,
            }));
          } else {
            formattedRoles.push({
              value: currentUserRole,
              label: currentUserRole,
            });
            setFormData((prev) => ({ ...prev, role: currentUserRole }));
          }
        }
        setRoleOptions(formattedRoles);
      } else {
        setRoleOptions([]);
      }
    } catch (error) {
      console.error("Error loading roles:", error);
      toast.error("Failed to load roles for this client");
      setRoleOptions([]);
    } finally {
      setLoadingRoles(false);
    }
  }, []);

  useEffect(() => {
    loadUserData();
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (formData.role && !isChiefRole(formData.role)) {
      loadClients();
    } else {
      setClients([]);
    }
  }, [formData.role]);

  const loadUserData = async () => {
    try {
      setLoading(true);
      const response = await userService.findUsers({ user_id: userId }, 1, 10);

      if (
        response.success &&
        response.data?.users &&
        response.data.users.length > 0
      ) {
        const userData = response.data.users[0];
        const userRole = userData.role_name || userData.role || "";
        const userClientId = userData.client_id || "";

        let normalizedDepartment = "";
        if (userData.department) {
          const dept = String(userData.department).trim();
          const matchedDept = departments.find(
            (d) => d.toLowerCase() === dept.toLowerCase(),
          );
          normalizedDepartment = matchedDept || dept;
        }

        const newFormData = {
          full_name: userData.full_name || "",
          email: userData.email || "",
          department: normalizedDepartment,
          role: userRole,
          client_id: userClientId,
        };

        setFormData(newFormData);

        if (userClientId && !isChiefRole(userRole)) {
          await loadRoles(userClientId, userRole);
        } else if (isChiefRole(userRole)) {
          const label = userRole || "Chief Admin";
          setRoleOptions([{ value: label, label }]);
        } else {
          setRoleOptions([]);
        }
      } else {
        toast.error("User not found");
        navigate("/users?tab=manage");
      }
    } catch (error) {
      console.error("Error loading user:", error);
      toast.error("Failed to load user data");
      navigate("/users?tab=manage");
    } finally {
      setLoading(false);
    }
  };

  const loadClients = async () => {
    try {
      setLoadingClients(true);
      const response = await clientService.getClients({
        page: 1,
        limit: 1000,
        is_active: true,
        sort_by: "client_name",
        sort_order: "asc",
      });

      if (response.success && response.data?.clients) {
        setClients(response.data.clients);
      } else {
        try {
          const dropdownResponse = await clientService.getClientsDropdown();
          if (dropdownResponse.success && dropdownResponse.data?.clients) {
            setClients(dropdownResponse.data.clients);
          }
        } catch (err) {
          console.error("Error loading clients:", err);
        }
      }
    } catch (error) {
      console.error("Error loading clients:", error);
    } finally {
      setLoadingClients(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.full_name.trim()) {
      newErrors.full_name = "Full name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Invalid email format";
    }

    if (!formData.role) {
      newErrors.role = "Role is required";
    }

    if (!isChiefRole(formData.role) && !formData.department) {
      newErrors.department = "Department is required";
    }

    if (!isChiefRole(formData.role) && !formData.client_id) {
      newErrors.client_id = "Client is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field) => (value) => {
    if (field === "client_id") {
      setFormData((prev) => {
        const next = { ...prev, client_id: value };
        if (value && !isChiefRole(prev.role)) {
          loadRoles(value, prev.role);
        }
        return next;
      });
    } else if (field === "role") {
      setFormData((prev) => {
        let next = { ...prev, role: value };
        if (isChiefRole(value)) {
          next = { ...next, client_id: "", department: "" };
          const label = value || "Chief Admin";
          setRoleOptions([{ value: label, label }]);
        } else if (value && next.client_id) {
          loadRoles(next.client_id, value);
        }
        return next;
      });
    } else {
      setFormData((prev) => ({ ...prev, [field]: value }));
    }

    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);

      const userData = {
        full_name: formData.full_name.trim(),
        email: formData.email.trim().toLowerCase(),
        role: formData.role,
      };

      if (!isChiefRole(formData.role)) {
        userData.department = formData.department;
        userData.client_id = formData.client_id;
      }

      await userService.updateUser(userId, userData);
      toast.success("User updated successfully");
      navigate("/users?tab=manage");
    } catch (error) {
      console.error("Error updating user:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to update user";
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="h-64 p-4">
        <FormSkeleton fields={6} />
      </div>
    );
  }

  const chief = isChiefRole(formData.role);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">Edit User</h1>
          <p className="text-text-secondary mt-1">Update user information</p>
        </div>
        <Button variant="outline" onClick={() => navigate("/users?tab=manage")}>
          Back
        </Button>
      </div>

      <div className="card">
        <form onSubmit={handleSubmit} className="space-y-6 p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label htmlFor="full_name">Full Name *</Label>
              <Input
                id="full_name"
                value={formData.full_name}
                onChange={(e) => handleChange("full_name")(e.target.value)}
                className={`mt-1 ${errors.full_name ? "border-red-500" : ""}`}
                required
              />
              {errors.full_name && (
                <p className="text-red-500 text-xs mt-1">{errors.full_name}</p>
              )}
            </div>

            <div>
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleChange("email")(e.target.value)}
                className={`mt-1 ${errors.email ? "border-red-500" : ""}`}
                required
              />
              {errors.email && (
                <p className="text-red-500 text-xs mt-1">{errors.email}</p>
              )}
            </div>

            <div>
              <Label htmlFor="department">
                Department {!chief && "*"}
              </Label>
              <Select
                value={formData.department}
                onValueChange={handleChange("department")}
                disabled={chief}
              >
                <SelectTrigger
                  className={`mt-1 ${errors.department ? "border-red-500" : ""}`}
                >
                  <SelectValue placeholder="Select department" />
                </SelectTrigger>
                <SelectContent>
                  {departments.map((dept) => (
                    <SelectItem key={dept} value={dept}>
                      {dept}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.department && (
                <p className="text-red-500 text-xs mt-1">{errors.department}</p>
              )}
            </div>

            <div>
              <Label htmlFor="role">Role *</Label>
              <Select
                value={formData.role}
                onValueChange={handleChange("role")}
                disabled={loadingRoles && roleOptions.length === 0 && !chief}
              >
                <SelectTrigger
                  className={`mt-1 ${errors.role ? "border-red-500" : ""}`}
                >
                  <SelectValue
                    placeholder={
                      loadingRoles ? "Loading roles…" : "Select role"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {loadingRoles && roleOptions.length === 0 ? (
                    <SelectItem value="__loading_roles__" disabled>
                      <div className="flex items-center gap-2 py-0.5">
                        <InlineLoadSkeleton className="h-4 w-40" />
                      </div>
                    </SelectItem>
                  ) : (
                    roleOptions.map((role) => (
                      <SelectItem key={role.value} value={role.value}>
                        {role.label}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              {errors.role && (
                <p className="text-red-500 text-xs mt-1">{errors.role}</p>
              )}
            </div>

            {formData.role && !chief && (
              <div className="md:col-span-2">
                <Label htmlFor="client_id">Client *</Label>
                <Select
                  value={formData.client_id}
                  onValueChange={handleChange("client_id")}
                  disabled={loadingClients}
                >
                  <SelectTrigger
                    className={`mt-1 ${errors.client_id ? "border-red-500" : ""}`}
                  >
                    <SelectValue placeholder="Select client" />
                  </SelectTrigger>
                  <SelectContent>
                    {loadingClients ? (
                      <SelectItem value="__loading_clients__" disabled>
                        <div className="flex items-center gap-2 py-0.5">
                          <InlineLoadSkeleton className="h-4 w-40" />
                        </div>
                      </SelectItem>
                    ) : (
                      clients.map((client) => (
                        <SelectItem
                          key={client.client_id}
                          value={client.client_id}
                        >
                          {client.client_name}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                {errors.client_id && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.client_id}
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-4 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/users?tab=manage")}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? (
                <>
                  <InlineLoadSkeleton className="h-4 w-4 mr-2" />
                  Update User
                </>
              ) : (
                "Update User"
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditUserPage;
